import { useState, useEffect, useMemo, useRef } from 'react';
import { getCustomerById, updateCustomer } from '@/lib/supabaseDb';
import { useToast } from './Toast';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { useAuth } from '@/contexts/AuthContext';
import ResponsiveFormModal from '@/components/base/ResponsiveFormModal';
import { capitalizeWords, digitsOnly } from '@/lib/inputHelpers';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';

interface CustomerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  focusNotes?: boolean;
  zIndex?: number;
}

const EDIT_DRAFT_PREFIX = 'polatlar_customer_edit_draft_';

export default function CustomerEditModal({ isOpen, onClose, customerId, focusNotes = false, zIndex = 65 }: CustomerEditModalProps) {
  const { showToast } = useToast();
  const { triggerRefresh } = useDataRefresh();
  const { businessCity, businessDistrict } = useAuth();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [secondPhone, setSecondPhone] = useState('');
  const [customerNo, setCustomerNo] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [address, setAddress] = useState('');
  const [identityNo, setIdentityNo] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('Aktif');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const draftChecked = useRef(false);

  const originalRef = useRef({ fullName: '', phone: '', secondPhone: '', city: '', district: '', neighborhood: '', address: '', identityNo: '', notes: '', status: 'Aktif' });

  const draftKey = useMemo(() => `${EDIT_DRAFT_PREFIX}${customerId}`, [customerId]);

  const isDirty = useMemo(() => {
    const o = originalRef.current;
    return fullName !== o.fullName || phone !== o.phone || secondPhone !== o.secondPhone || city !== o.city || district !== o.district || neighborhood !== o.neighborhood || address !== o.address || identityNo !== o.identityNo || notes !== o.notes || status !== o.status;
  }, [fullName, phone, secondPhone, city, district, neighborhood, address, identityNo, notes, status]);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    async function load() {
      const res = await getCustomerById(customerId);
      const customer = res.data;
      if (customer) {
        // Müşterinin kendi il/ilçesi yoksa işletmenin kayıtlı il/ilçesi
        // varsayılan öneri olarak gösterilir (kullanıcı değiştirebilir).
        // Mahalle için böyle bir varsayılan öneri yok, her zaman boş başlar.
        const vals = { fullName: customer.full_name || '', phone: customer.phone || '', secondPhone: customer.second_phone || '', city: customer.city || businessCity || '', district: customer.district || businessDistrict || '', neighborhood: customer.neighborhood || '', address: customer.address || '', identityNo: customer.identity_number || '', notes: customer.notes || '', status: customer.status || 'Aktif' };
        originalRef.current = vals;
        setFullName(vals.fullName); setPhone(vals.phone); setSecondPhone(vals.secondPhone); setCustomerNo(customer.customer_no || ''); setCity(vals.city); setDistrict(vals.district); setNeighborhood(vals.neighborhood); setAddress(vals.address); setIdentityNo(vals.identityNo); setNotes(vals.notes); setStatus(vals.status);
        setErrors({}); setSaving(false);
      }
    }
    load();
  }, [isOpen, customerId, businessCity, businessDistrict]);

  useEffect(() => {
    if (focusNotes && isOpen) {
      setTimeout(() => {
        const textarea = document.getElementById('cust-edit-notes') as HTMLTextAreaElement;
        if (textarea) { textarea.focus(); textarea.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      }, 150);
    }
  }, [focusNotes, isOpen]);

  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (!isDirty) { sessionStorage.removeItem(draftKey); return; }
      const draft = { fullName, phone, secondPhone, city, district, neighborhood, address, identityNo, notes, status };
      sessionStorage.setItem(draftKey, JSON.stringify(draft));
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [isOpen, isDirty, draftKey, fullName, phone, secondPhone, city, district, neighborhood, address, identityNo, notes, status]);

  useEffect(() => {
    if (!isOpen) { draftChecked.current = false; return; }
    const timer = setTimeout(() => {
      if (draftChecked.current) return;
      const saved = sessionStorage.getItem(draftKey);
      if (!saved) return;
      try {
        const draft = JSON.parse(saved);
        const o = originalRef.current;
        const hasChanges = (draft.fullName || '') !== o.fullName || (draft.phone || '') !== o.phone || (draft.secondPhone || '') !== o.secondPhone || (draft.city || '') !== o.city || (draft.district || '') !== o.district || (draft.neighborhood || '') !== o.neighborhood || (draft.address || '') !== o.address || (draft.identityNo || '') !== o.identityNo || (draft.notes || '') !== o.notes || (draft.status || '') !== o.status;
        if (hasChanges) setShowDraftPrompt(true);
      } catch { /* ignore */ }
      draftChecked.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen, draftKey]);

  useEffect(() => {
    if (!isOpen || !isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isOpen, isDirty]);

  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ modal: 'customer-edit', ts: Date.now() }, '');
    const handler = () => {
      if (isDirty) { window.history.pushState({ modal: 'customer-edit', ts: Date.now() }, ''); setShowExitConfirm(true); }
      else { sessionStorage.removeItem(draftKey); onClose(); }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isOpen, isDirty, draftKey, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (isDirty) setShowExitConfirm(true); else { sessionStorage.removeItem(draftKey); onClose(); } }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [isOpen, isDirty, draftKey, onClose]);

  const inputClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
  const inputErrorClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-red-50 border border-red-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-foreground-800 placeholder:text-foreground-300';
  const selectClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';
  const errorTextClass = 'text-xs text-red-500 mt-1';
  const readonlyClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-100 border border-background-200 rounded-lg text-foreground-600 cursor-not-allowed';
  const helperTextClass = 'text-[10px] text-foreground-300 mt-0.5';

  function validatePhone(val: string): string {
    if (!val.trim()) return 'Telefon numarası zorunludur.';
    const cleaned = val.replace(/\s/g, '').replace(/^0/, '');
    if (cleaned.length < 10) return 'Telefon numarası en az 10 karakter olmalıdır.';
    if (!/^5\d{9}$/.test(cleaned)) return 'Geçerli bir Türkiye telefon numarası giriniz (5XX XXX XX XX).';
    return '';
  }

  function validateForm(): boolean {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.full_name = 'Ad Soyad zorunludur.';
    const phoneErr = validatePhone(phone);
    if (phoneErr) errs.phone = phoneErr;
    if (secondPhone.trim()) { const spErr = validatePhone(secondPhone); if (spErr) errs.second_phone = spErr; }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function clearDraft() { sessionStorage.removeItem(draftKey); }

  function handleClose() { if (saving) return; if (isDirty) { setShowExitConfirm(true); return; } handleForceClose(); }
  function handleForceClose() { clearDraft(); setShowExitConfirm(false); setShowDraftPrompt(false); onClose(); }

  function handleDraftContinue() {
    const saved = sessionStorage.getItem(draftKey);
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.fullName !== undefined) setFullName(d.fullName);
        if (d.phone !== undefined) setPhone(d.phone);
        if (d.secondPhone !== undefined) setSecondPhone(d.secondPhone);
        if (d.city !== undefined) setCity(d.city);
        if (d.district !== undefined) setDistrict(d.district);
        if (d.neighborhood !== undefined) setNeighborhood(d.neighborhood);
        if (d.address !== undefined) setAddress(d.address);
        if (d.identityNo !== undefined) setIdentityNo(d.identityNo);
        if (d.notes !== undefined) setNotes(d.notes);
        if (d.status !== undefined) setStatus(d.status);
      } catch { /* ignore */ }
    }
    setShowDraftPrompt(false);
  }

  function handleDraftDiscard() { clearDraft(); setShowDraftPrompt(false); }

  async function handleSave() {
    if (saving) return;
    if (!validateForm()) return;
    setSaving(true);

    try {
      const result = await updateCustomer(customerId, { full_name: fullName.trim(), phone: phone.trim(), second_phone: secondPhone.trim(), city: city.trim(), district: district.trim(), neighborhood: neighborhood.trim(), address: address.trim(), identity_number: identityNo.trim(), notes: notes.trim(), status });
      if (result.error) { showToast('Müşteri bilgileri güncellenemedi.', 'error'); setSaving(false); return; }
      clearDraft();
      showToast('Müşteri bilgileri başarıyla güncellendi.', 'success');
      triggerRefresh();
      onClose();
    } catch {
      showToast('Müşteri bilgileri güncellenemedi.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const headerContent = (
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0"><i className="ri-edit-line text-background-50 text-sm"></i></span>
        <div><h3 className="text-base font-semibold text-foreground-900">Müşteriyi Düzenle</h3><p className="text-xs text-foreground-400">{fullName}</p></div>
      </div>
      <button onClick={handleClose} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer disabled:opacity-50"><i className="ri-close-line"></i></button>
    </div>
  );

  const footerContent = (
    <div className="flex items-center justify-end gap-3 px-5 py-3">
      <button onClick={handleClose} disabled={saving} className="btn-outline cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm">İptal</button>
      <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm flex-1 lg:flex-none justify-center">
        {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>Kaydet</>}
      </button>
    </div>
  );

  const overlayNode = showDraftPrompt ? (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4"><span className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><i className="ri-file-history-line text-amber-600 text-lg"></i></span><div><h4 className="text-sm font-semibold text-foreground-900">Kaydedilmemiş Taslak</h4><p className="text-xs text-foreground-500">Kaydedilmemiş düzenleme taslağı bulundu.</p></div></div>
      <div className="flex gap-2"><button onClick={handleDraftDiscard} className="flex-1 py-2 px-3 text-sm rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap">Taslağı Sil</button><button onClick={handleDraftContinue} className="flex-1 py-2 px-3 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap">Taslağı Devam Ettir</button></div>
    </div>
  ) : showExitConfirm ? (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4"><span className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0"><i className="ri-alert-line text-red-500 text-lg"></i></span><div><h4 className="text-sm font-semibold text-foreground-900">Kaydedilmemiş Değişiklikler</h4><p className="text-xs text-foreground-500 mt-0.5">Yaptığınız değişiklikler kaybolacak.</p></div></div>
      <div className="flex gap-2"><button onClick={handleForceClose} className="flex-1 py-2 px-3 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap">Değişiklikleri Sil ve Çık</button><button onClick={() => setShowExitConfirm(false)} className="flex-1 py-2 px-3 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap">Forma Geri Dön</button></div>
    </div>
  ) : null;

  return (
    <ResponsiveFormModal
      isOpen={isOpen}
      onClose={handleClose}
      zIndex={zIndex}
      header={headerContent}
      footer={footerContent}
      overlayContent={overlayNode}
    >
      <div className="px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={labelClass}>Ad Soyad <span className="text-red-400">*</span></label><input type="text" value={fullName} onChange={e => { setFullName(capitalizeWords(e.target.value)); setErrors(prev => ({ ...prev, full_name: '' })); }} className={errors.full_name ? inputErrorClass : inputClass} placeholder="Ad Soyad" />{errors.full_name && <p className={errorTextClass}>{errors.full_name}</p>}</div>
          <div><label className={labelClass}>Müşteri No</label><div className={readonlyClass}>{customerNo || '—'}</div><p className={helperTextClass}>Salt okunur</p></div>
          <div><label className={labelClass}>Müşteri Durumu</label><select value={status} onChange={e => setStatus(e.target.value)} className={selectClass}><option value="Aktif">Aktif</option><option value="Borçlu">Borçlu</option><option value="Borcu Yok">Borcu Yok</option><option value="Kara Liste">Kara Liste</option></select></div>
          <div><label className={labelClass}>1. Telefon <span className="text-red-400">*</span></label><input type="text" value={phone} onChange={e => { setPhone(digitsOnly(e.target.value)); setErrors(prev => ({ ...prev, phone: '' })); }} className={errors.phone ? inputErrorClass : inputClass} placeholder="05XX XXX XX XX" />{errors.phone && <p className={errorTextClass}>{errors.phone}</p>}</div>
          <div><label className={labelClass}>2. Telefon</label><input type="text" value={secondPhone} onChange={e => { setSecondPhone(digitsOnly(e.target.value)); setErrors(prev => ({ ...prev, second_phone: '' })); }} className={errors.second_phone ? inputErrorClass : inputClass} placeholder="05XX XXX XX XX" />{errors.second_phone && <p className={errorTextClass}>{errors.second_phone}</p>}</div>
          <div className="sm:col-span-2"><CityDistrictSelect city={city} district={district} neighborhood={neighborhood} onCityChange={setCity} onDistrictChange={setDistrict} onNeighborhoodChange={setNeighborhood} selectClassName={selectClass} /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Adres</label><textarea value={address} onChange={e => setAddress(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Açık adres..."></textarea></div>
          <div><label className={labelClass}>TC Kimlik No</label><input type="text" value={identityNo} onChange={e => setIdentityNo(e.target.value)} className={inputClass} placeholder="11 haneli" maxLength={11} /></div>
          <div className="sm:col-span-2"><label className={labelClass}>Müşteri Notu</label><textarea id="cust-edit-notes" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-24`} placeholder="Notlar..."></textarea></div>
        </div>
      </div>
    </ResponsiveFormModal>
  );
}